import React, { useMemo } from 'react'

import { useQuery } from '@apollo/client'

import NoContent from '@web/components/NoContent'
import { NoContentTypes } from '@web/components/NoContent/type'
import { gql } from '@web/gql'
import { UserRelationshipType } from '@web/gql/graphql'
import { useGlobalStaticStateContext } from '@web/hooks/useGlobalStaticState'
import usePageInfo from '@web/hooks/usePageInfo'
import { extractByTypename } from '@web/libs/apollo/typeGuard'

import UserRelationshipList from './UserRelationshipList'
import styles from './UserRelationships.module.scss'
interface Props {
  type: UserRelationshipType.Block | UserRelationshipType.Following | UserRelationshipType.Follower
}

export const UserRelationshipsQuery = gql(`
  query UserRelationships($idno: String!, $type: UserRelationshipType!, $after: String) {
    userRelationships(input: { after: $after, type: $type, idno: $idno }) {
      ...UserRelationshipPage
    }
  }
`)

const UserRelationships = ({ type }: Props) => {
  const { isMobile } = useGlobalStaticStateContext()

  const { pageOwner } = usePageInfo()

  const { data, loading, fetchMore } = useQuery(UserRelationshipsQuery, {
    fetchPolicy: 'cache-and-network',
    variables: {
      idno: pageOwner!.idno,
      type,
    },
  })

  const { UserRelationshipsSucceed } = extractByTypename(data?.userRelationships)

  const fetchMoreRelationships = () => {
    if (!UserRelationshipsSucceed) return

    fetchMore({
      variables: {
        after: UserRelationshipsSucceed.connection.pageInfo.endCursor,
      },
    })
  }

  const users = UserRelationshipsSucceed?.connection.edges.map(({ user }) => user) ?? []
  const hasMoreCursor = Number(UserRelationshipsSucceed?.connection.pageInfo.endCursor) > 0

  const totalUsers = useMemo(() => {
    const typeTotalMap = {
      [UserRelationshipType.Block]: pageOwner?.stat.blockCount ?? 0,
      [UserRelationshipType.Follower]: pageOwner?.stat.followerCount ?? 0,
      [UserRelationshipType.Following]: pageOwner?.stat.followingCount ?? 0,
    }
    return typeTotalMap[type]
  }, [type, pageOwner])

  if (totalUsers === 0) {
    const noContentTypeMap = {
      [UserRelationshipType.Block]: NoContentTypes.BLOCK,
      [UserRelationshipType.Follower]: NoContentTypes.FOLLOWERS,
      [UserRelationshipType.Following]: NoContentTypes.FOLLOWING,
    }

    return <NoContent type={noContentTypeMap[type]} />
  }

  return (
    <div className="place_section">
      <div className={styles.inner}>
        {users.length > 0 && (
          <UserRelationshipList
            users={users}
            type={type}
            loading={loading}
            fetchMore={fetchMoreRelationships}
            totalUsers={totalUsers}
            useInfiniteScroll={isMobile}
            hasMoreCursor={hasMoreCursor}
          />
        )}
      </div>
    </div>
  )
}

export default UserRelationships
